<!--
  简历问答页面：左侧展示 docs 中维护的简历 Markdown，右侧提供 AI 面试问答。
-->
<template>
  <div class="resume-page" :class="{ 'resume-page--collapsed': isResumeCollapsed }">
    <div class="resume-sidebar">
      <article v-show="!isResumeCollapsed" class="resume-content" v-html="resumeHtml"></article>
      <el-backtop v-if="!isResumeCollapsed" target=".resume-sidebar" :right="24" :bottom="24" :visibility-height="240"
        class="resume-backtop" />
    </div>

    <div class="resume-collapse-rail">
      <el-button circle class="resume-toggle" :icon="isResumeCollapsed ? Expand : Fold"
        :title="isResumeCollapsed ? '展开简历' : '收起简历'" :aria-label="isResumeCollapsed ? '展开简历' : '收起简历'"
        @click="toggleResume" />
    </div>

    <div class="chat-panel">
      <div class="chat-header">
        <h2 class="chat-header-title">
          <!-- <span class="chat-header-dot"></span> -->
          AI 面试助手（基于简历与项目经历）
        </h2>
        <p class="chat-header-tip">
          你可以直接问我关于 Jing 的项目细节、技术难点或业务思考。所有问答记录会自动同步到便签库。
        </p>
      </div>

      <div class="chat-messages" ref="chatContainer">
        <div v-for="m in messages" :key="m.id" class="chat-message"
          :class="m.role === 'user' ? 'chat-message--user' : 'chat-message--assistant'">
          <div v-if="m.role === 'user'" class="chat-bubble chat-bubble--user">
            <p class="whitespace-pre-wrap">{{ m.content }}</p>
          </div>

          <div v-else class="chat-bubble chat-bubble--assistant">
            <div class="message-text markdown-body" v-html="renderMarkdown(m.content)"></div>
          </div>
        </div>

        <div v-if="isLoading" class="chat-message chat-message--assistant">
          <div class="chat-loading">
            <span class="chat-loading-dot delay-100"></span>
            <span class="chat-loading-dot delay-200"></span>
            <span class="chat-loading-dot delay-300"></span>
          </div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit" class="chat-footer">
        <div class="chat-input-wrapper">
          <el-popover v-model:visible="isQuestionPopoverVisible" placement="top-start" trigger="click" :width="360"
            :teleported="false" popper-class="quick-questions-popper">
            <template #reference>
              <button type="button" class="quick-question-trigger" :disabled="isLoading" title="快捷问题"
                aria-label="打开快捷问题">
                <el-icon>
                  <QuestionFilled />
                </el-icon>
                <span>问题</span>
              </button>
            </template>

            <div class="quick-questions">
              <button v-for="question in suggestedQuestions" :key="question" type="button" class="quick-question"
                :disabled="isLoading" @click="handleSuggestedQuestion(question)">
                {{ question }}
              </button>
            </div>
          </el-popover>

          <input v-model="input" placeholder="输入你想了解的项目、技术难点或业务思考" class="chat-input" />
          <button type="submit" :disabled="isLoading || !input.trim()" class="chat-btn">
            发送
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useChat } from "@ai-sdk/vue";
import { Expand, Fold, QuestionFilled } from "@element-plus/icons-vue";
import resumeMarkdown from "~/docs/3_面试_Web前端工程师-郑静.md?raw";
import { renderMarkdown } from "~/utils/index";

const resumeHtml = computed(() => renderMarkdown(resumeMarkdown));
const isResumeCollapsed = ref(false);
const isQuestionPopoverVisible = ref(false);

// 示例问题
const suggestedQuestions = [
  "请用 2 分钟介绍一下 Jing 最有代表性的项目经历",
  "AgentDesk 这个 Nuxt 3 全栈 AI 项目解决了哪些工程问题？",
  "Chrome 扩展 AI 助手里，Jing 负责了哪些核心设计？",
  "她在海关 B/G 端复杂业务系统里体现了哪些前端能力？",
  "能举例说明她做过的性能优化和组件封装吗？",
];

const { messages, input, handleSubmit, isLoading, append } = useChat({
  api: "/api/resume/chat",
  initialMessages: [
    {
      id: "welcome",
      role: "assistant",
      content:
        "你好，我是 Jing 的 AI 面试助手，会基于她的简历和项目经历回答问题。你可以了解她在 Nuxt 3 全栈 AI 项目、Chrome 扩展 AI 应用、海关 B/G 端系统、复杂业务组件封装和前端性能优化方面的实践。",
    },
  ],
});

const chatContainer = ref<HTMLElement | null>(null);

const toggleResume = () => {
  isResumeCollapsed.value = !isResumeCollapsed.value;
};

const handleSuggestedQuestion = async (question: string) => {
  if (isLoading.value) return;

  isQuestionPopoverVisible.value = false;

  await append({
    role: "user",
    content: question,
  });
};

watch(
  messages,
  () => {
    nextTick(() => {
      if (chatContainer.value) {
        chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
      }
    });
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
@use "@/assets/styles/pages/resume.scss";
</style>
